import { createContext, memo, useContext, useMemo, ReactNode } from 'react';
import { ValidListFilterType, PartialVLF } from '../../types';
import { useLocalAdvancedFilters } from 'components/Search/hooks/useLocalAdvancedFilters';

export type LocalAdvancedSearchContextType = {
  stagedFilters: PartialVLF;
  setStagedFilters: React.Dispatch<React.SetStateAction<PartialVLF>>;
  updateStagedFilterValue: (
    key: keyof ValidListFilterType,
    value: string | string[],
  ) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  [key: string]: any;
};

export const LocalAdvancedSearchContext = createContext<
  LocalAdvancedSearchContextType | undefined
>(undefined);

export const useLocalAdvancedSearch = () => {
  const context = useContext(LocalAdvancedSearchContext);
  if (!context) {
    throw new Error(
      'useLocalAdvancedSearch must be used within a LocalAdvancedSearchContext',
    );
  }
  return context;
};

export type LocalAdvancedSearchProviderProps = {
  children: ReactNode;
  additionalContext?: Record<string, any>;
  initialFilters?: PartialVLF;
  onApplyLocalState?: (filters: PartialVLF) => void;
  externalStagedFilters?: PartialVLF;
  externalSetStagedFilters?: React.Dispatch<React.SetStateAction<PartialVLF>>;
};

/**
 * provider for advanced search filters supporting local-state only
 * strategy to not conflict with the main URL based search context.
 */
export const LocalAdvancedSearchProvider = memo(
  ({
    children,
    additionalContext = {},
    initialFilters,
    onApplyLocalState,
    externalStagedFilters,
    externalSetStagedFilters,
  }: LocalAdvancedSearchProviderProps) => {
    const advancedFilters = useLocalAdvancedFilters({
      initialFilters,
      onApplyLocalState,
      externalStagedFilters,
      externalSetStagedFilters,
    });

    const contextValue = useMemo(
      () => ({
        ...advancedFilters,
        ...additionalContext,
      }),
      [advancedFilters, additionalContext],
    );

    return (
      <LocalAdvancedSearchContext.Provider value={contextValue}>
        {children}
      </LocalAdvancedSearchContext.Provider>
    );
  },
);

LocalAdvancedSearchProvider.displayName = 'LocalAdvancedSearchProvider';
